import React from 'react';
import { capitalizeWords } from '@/modules/TCIUtils';
import I18n from 'i18n-js';
import AIReadingLevelBanner, {
  type OrphanedAiData,
} from '@/components/shared/AIReadingLevelBanner/AIReadingLevelBanner';
import TableOfContentsButton from '@/components/shared/TableOfContents/TableOfContentsButton';
import Text from '@/components/ui/Text';
import { buttonFocusRing } from '@/components/ui/ui.constants';
import UnitSection from './UnitSection';
import ToDoSection from './ToDoSection';
import StudentWorkSection from './StudentWorkSection';
import ImpersonateShortcut from '../../common/ShortcutButtons/ImpersonateShortcut';

import { ProgramType, UserType } from './types';

interface NewDashboardProps {
  classroomId: number;
  coTeacherVisibility: boolean;
  hasUnits: boolean;
  lessonId: number;
  orphanedAiData?: OrphanedAiData | null;
  overviewColor: string;
  program: ProgramType;
  stafferMasquerade: boolean;
  usePoppinsFont: boolean;
  user: UserType;
}

const NewDashboard = ({
  classroomId, coTeacherVisibility, hasUnits, lessonId, orphanedAiData, overviewColor, program, stafferMasquerade,
  usePoppinsFont, user,
}: NewDashboardProps) => {
  const isStudent = user.type === 'Student';

  return (
    <div className="tw-bg-white">
      <div className="tw-box-border tw-flex tw-flex-row tw-flex-wrap tw-items-center tw-justify-between
        tw-gap-y-2 tw-px-5 tw-w-full"
      >
        <Text tag="h1" variant={usePoppinsFont ? 'heading2' : 'heading3'}>
          {I18n.t('welcome')}, {capitalizeWords(user.firstName || user.name)}!
        </Text>
        <div className="tw-flex tw-items-center tw-gap-2 tw-ml-auto">
          <TableOfContentsButton
            programId={program.id}
          />
          {!isStudent && (
            <div className="tw-flex tw-justify-end">
              <ImpersonateShortcut
                buttonClass={`tw-bg-white tw-border tw-border-tci-purple tw-rounded tw-cursor-pointer tw-flex
                  tw-h-10 tw-w-10 tw-items-center tw-justify-center
                  hover:tw-bg-tci-purple [&:hover_img]:tw-brightness-0 [&:hover_img]:tw-invert
                  active:tw-bg-dark-purple [&:active_img]:tw-brightness-0 [&:active_img]:tw-invert
                  ${buttonFocusRing}`}
                enabled={!stafferMasquerade}
                programId={program.id}
                stafferId={user.id}
              />
            </div>
          )}
        </div>
      </div>
      {!isStudent && (
        <div className="tw-px-5">
          <AIReadingLevelBanner orphanedAiData={orphanedAiData ?? null} programId={program.id} />
        </div>
      )}
      <ToDoSection
        classroomId={classroomId}
        coTeacherVisibility={coTeacherVisibility}
        lessonId={lessonId}
        programId={program.id}
        user={user}
      />
      {!isStudent && (
        <StudentWorkSection
          lessonId={lessonId}
          programId={program.id}
        />
      )}
      {hasUnits && (
        <UnitSection color={overviewColor} programId={program.id} />
      )}
    </div>
  );
};

export default NewDashboard;
